/**
 * Φύλλο επιλογών είδους στο κινητό του σερβιτόρου — πάντα ενεργό από 6/10/2026 (ως τότε πίσω από
 * το dine_in_settings.waiter_customization_enabled, migration 244).
 *
 * - Ομάδες επιλογών όπως στην οθόνη τραπεζιών του ταμία: single = μία, multiple = πολλές.
 *   Οι υποχρεωτικές ΔΕΝ αφήνουν «Προσθήκη» χωρίς επιλογή (single: ακριβώς μία,
 *   multiple: τουλάχιστον μία). Προεπιλογή ΜΟΝΟ οι is_default — όχι «η πρώτη» κάθε ομάδας,
 *   αλλιώς κάθε ποτό θα έβγαινε «ΣΟΔΑ» και κάθε κυρίως «RARE».
 * - «Χωρίς Χ» για τα προεπιλεγμένα απλά υλικά (utils/simpleIngredients).
 * - Τιμή: utils/dineInMoney — η ΙΔΙΑ συνάρτηση με το ταμείο.
 * Έξτρα υλικών δεν μπαίνουν από το PDA (μόνο από το ταμείο).
 *
 * Από 7/10/2026 (ιδιοκτήτης): στην προσθήκη ανοίγει ΜΟΝΟ με τις υποχρεωτικές ομάδες· οι υπόλοιπες, το
 * «χωρίς» και η σημείωση αλλάζουν μετά, από τη μπάρα/σύνοψη — με το ίδιο φύλλο σε λειτουργία «αλλαγής»
 * (mode="edit": τρέχουσες επιλογές της γραμμής, «Αποθήκευση», και «Μόνο σε 1 / Σε όλα» με 2+ τεμάχια).
 */

import React, { useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import type { PosOptionGroup, SelectedOptionEntry, SimpleIngredient } from '../../../types/order';
import { roundToCents, unitPriceWithAddOns } from '../../../utils/dineInMoney';
import { Sheet, Button, money } from './ui';

export interface RemovedIngredient {
  id: number;
  name: string;
}

export interface CustomizeResult {
  /** Σειρά: ομάδα, μετά επιλογή — όπως φαίνονται στο φύλλο. */
  options: SelectedOptionEntry[];
  removed: RemovedIngredient[];
}

/** Προσαύξηση επιλογής: «+0,30 €». */
export const surcharge = (n: number): string => `+${n.toFixed(2).replace('.', ',')} €`;

type Choice = PosOptionGroup['choices'][number];

const entryOf = (group: PosOptionGroup, choice: Choice): SelectedOptionEntry => ({
  group_id: group.id,
  group_name: group.name,
  choice_id: choice.id,
  choice_name: choice.name,
  price_modifier: Number(choice.price_modifier) || 0,
});

/** Υποχρεωτικές ομάδες χωρίς έγκυρη επιλογή: single → ακριβώς μία, multiple → τουλάχιστον μία. */
export function missingRequiredGroups(groups: PosOptionGroup[], selected: SelectedOptionEntry[]): PosOptionGroup[] {
  return groups.filter(group => {
    if (!group.is_required) return false;
    const count = selected.filter(o => o.group_id === group.id).length;
    return group.selection_type === 'multiple' ? count < 1 : count !== 1;
  });
}

/** Προεπιλογές: ΜΟΝΟ όσες επιλογές είναι is_default (σε single το πολύ μία, η πρώτη τέτοια). */
export function defaultSelections(groups: PosOptionGroup[]): SelectedOptionEntry[] {
  const out: SelectedOptionEntry[] = [];
  for (const group of groups) {
    const defaults = group.choices.filter(c => c.is_default === true);
    for (const choice of group.selection_type === 'multiple' ? defaults : defaults.slice(0, 1)) {
      out.push(entryOf(group, choice));
    }
  }
  return out;
}

/** Σε γραμμή με 2+ τεμάχια: η αλλαγή σε ένα (γίνεται δική του γραμμή) ή σε όλα. */
export type EditScope = 'one' | 'all';

interface CustomizeSheetProps {
  title: string;
  subtitle?: string;
  /** Τιμή του είδους στο μέγεθος που διαλέχτηκε, χωρίς επιλογές. */
  basePrice: number;
  groups: PosOptionGroup[];
  /** Προεπιλεγμένα απλά υλικά που αφαιρούνται (utils/simpleIngredients.removableSimpleIngredients). */
  removable: SimpleIngredient[];
  /** add = νέο είδος (προεπιλογές is_default)· edit = αλλαγή γραμμής (οι τρέχουσες επιλογές της). */
  mode?: 'add' | 'edit';
  /** edit: οι επιλογές/«χωρίς» της γραμμής τώρα. */
  initialOptions?: SelectedOptionEntry[];
  initialRemoved?: number[];
  /** edit: τεμάχια της γραμμής — με 2+ το φύλλο ρωτά «Μόνο σε 1» ή «Σε όλα». */
  quantity?: number;
  /** Επιλογές που μετρούν στην τιμή του κουμπιού χωρίς να φαίνονται εδώ (π.χ. οι ήδη διαλεγμένες
   *  όταν αλλάζει μόνο το «χωρίς») — ώστε το ποσό να είναι πάντα το πραγματικό. */
  priceOptions?: SelectedOptionEntry[];
  onClose: () => void;
  onConfirm: (result: CustomizeResult, scope: EditScope) => void;
}

const CustomizeSheet: React.FC<CustomizeSheetProps> = ({
  title,
  subtitle,
  basePrice,
  groups,
  removable,
  mode = 'add',
  initialOptions,
  initialRemoved,
  quantity = 1,
  priceOptions = [],
  onClose,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<SelectedOptionEntry[]>(() =>
    mode === 'edit'
      ? (initialOptions ?? []).filter((o) => groups.some((g) => g.id === o.group_id))
      : defaultSelections(groups)
  );
  const [removedIds, setRemovedIds] = useState<number[]>(() => (mode === 'edit' ? initialRemoved ?? [] : []));

  const isSelected = (group: PosOptionGroup, choice: Choice) =>
    selected.some(o => o.group_id === group.id && o.choice_id === choice.id);

  const toggleChoice = (group: PosOptionGroup, choice: Choice) => {
    const on = isSelected(group, choice);
    if (group.selection_type === 'multiple') {
      setSelected(prev => (on
        ? prev.filter(o => !(o.group_id === group.id && o.choice_id === choice.id))
        : [...prev, entryOf(group, choice)]));
      return;
    }
    // single: αντικαθιστά· ξανά πάτημα αφαιρεί — εκτός αν η ομάδα είναι υποχρεωτική.
    if (on) {
      if (!group.is_required) setSelected(prev => prev.filter(o => o.group_id !== group.id));
      return;
    }
    setSelected(prev => [...prev.filter(o => o.group_id !== group.id), entryOf(group, choice)]);
  };

  const toggleRemoved = (id: number) =>
    setRemovedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  // Σειρά αποτελέσματος = σειρά εμφάνισης (σταθερό κλειδί γραμμής καλαθιού, ίδια εικόνα στην κουζίνα).
  const orderedOptions = groups.flatMap(group =>
    group.choices.filter(c => isSelected(group, c)).map(c => entryOf(group, c))
  );
  const missing = missingRequiredGroups(groups, selected);
  const unitPrice = roundToCents(unitPriceWithAddOns(basePrice, [...orderedOptions, ...priceOptions]));

  const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
  const changed = mode === 'add'
    || !sameSet(orderedOptions.map((o) => `${o.group_id}.${o.choice_id}`), (initialOptions ?? [])
      .filter((o) => groups.some((g) => g.id === o.group_id)).map((o) => `${o.group_id}.${o.choice_id}`))
    || !sameSet(removedIds.map(String), (initialRemoved ?? []).map(String));

  const confirm = (scope: EditScope) => {
    if (missing.length > 0) return;
    // Αλλαγή χωρίς τίποτα να αλλάξει = απλώς κλείσιμο (όχι «σπάσιμο» γραμμής σε δύο ίδιες).
    if (!changed) {
      onClose();
      return;
    }
    onConfirm({
      options: orderedOptions,
      removed: removable
        .filter(i => removedIds.includes(Number(i.store_ingredient_id)))
        .map(i => ({ id: Number(i.store_ingredient_id), name: i.name })),
    }, scope);
  };

  return (
    <Sheet
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <div className="space-y-2 pb-1">
          {missing.length > 0 && (
            <p className="px-1 text-sm font-semibold text-amber-700 dark:text-amber-400">
              Διάλεξε: {missing.map(g => g.name).join(', ')}
            </p>
          )}
          {mode === 'edit' && quantity > 1 && changed ? (
            <>
              <p className="px-1 text-sm text-t3">
                Τιμή τεμαχίου μετά την αλλαγή: <span className="font-bold text-t1 tabular-nums">{money(unitPrice)}</span>
              </p>
              <div className="flex gap-2">
                <Button tone="neutral" className="flex-1" disabled={missing.length > 0} onClick={() => confirm('one')}>
                  Μόνο σε 1
                </Button>
                <Button tone="primary" className="flex-1" disabled={missing.length > 0} onClick={() => confirm('all')}>
                  Σε όλα ({quantity})
                </Button>
              </div>
            </>
          ) : (
            <Button tone="primary" full disabled={missing.length > 0} onClick={() => confirm('all')}>
              {mode === 'edit' ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              <span className="flex-1 text-left">{mode === 'edit' ? 'Αποθήκευση' : 'Προσθήκη'}</span>
              <span className="tabular-nums">{money(unitPrice)}</span>
            </Button>
          )}
        </div>
      }
    >
      <div className="pb-3">
        {groups.map(group => {
          const groupMissing = missing.some(g => g.id === group.id);
          return (
            <section key={group.id} className="px-3 pt-3" aria-label={group.name}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h3 className="font-bold text-base text-t1">{group.name}</h3>
                {group.is_required && (
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      groupMissing
                        ? 'bg-amber-500 text-white'
                        : 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                    }`}
                  >
                    Υποχρεωτικό
                  </span>
                )}
                <span className="text-xs text-t4">{group.selection_type === 'multiple' ? 'μία ή περισσότερες' : 'μία'}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {group.choices.map(choice => {
                  const on = isSelected(group, choice);
                  const price = Number(choice.price_modifier) || 0;
                  return (
                    <button
                      key={choice.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleChoice(group, choice)}
                      className={`min-h-[48px] px-3 py-2 rounded-xl border-2 text-left flex flex-col justify-center active:scale-[0.98] transition-transform ${
                        on
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-line bg-surface-1 active:bg-surface-2'
                      }`}
                    >
                      <span className="font-bold text-[15px] text-t1 leading-tight break-words">{choice.name}</span>
                      {price > 0 && (
                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 tabular-nums">{surcharge(price)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}

        {removable.length > 0 && (
          <section className="px-3 pt-3" aria-label="Χωρίς">
            <h3 className="font-bold text-base text-t1 mb-2">Χωρίς</h3>
            <div className="grid grid-cols-2 gap-2">
              {removable.map(ingredient => {
                const id = Number(ingredient.store_ingredient_id);
                const off = removedIds.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={off}
                    onClick={() => toggleRemoved(id)}
                    className={`min-h-[48px] px-3 py-2 rounded-xl border-2 text-left flex items-center gap-2 active:scale-[0.98] transition-transform ${
                      off
                        ? 'border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/30'
                        : 'border-line bg-surface-1 active:bg-surface-2'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center ${
                        off ? 'bg-red-500' : 'bg-emerald-500'
                      }`}
                    >
                      {off ? <X className="w-3 h-3 text-white" /> : <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span
                      className={`font-semibold text-[15px] leading-tight break-words ${
                        off ? 'text-red-700 dark:text-red-400' : 'text-t1'
                      }`}
                    >
                      {off ? `χωρίς ${ingredient.name}` : ingredient.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </Sheet>
  );
};

export default CustomizeSheet;
